/* LMC Orlando — Quick View modal (Chunk 8).

   <lmc-quick-view> is the persistent modal shell rendered once in
   layout/theme.liquid. This script:
   - Delegates clicks on any [data-quick-view-trigger] in the document
   - Lazy-fetches the full /products/{handle}?view=quick-view page
     (rendered through layout/quick-view.liquid) and extracts the
     <product-info> body from it
   - Injects that into [data-qv-content]
   - Wires the thumbnail → main-image gallery swap
   - Locks body scroll, opens the modal, sets up Esc + scrim close + focus trap

   We fetch the full quick-view page rather than the Section Rendering API
   (?section_id=) because the API renders the section from the *default*
   product template, where it has no blocks — so the details column (title,
   price, swatches, Add to Cart) comes back empty. The quick-view template
   (product.quick-view.json) is what carries those blocks.

   Fetched HTML is cached per handle so reopening the same product is
   instant. The modal content needs Dawn's product-info.js +
   product-form.js + global.js (variant-selects) — those are loaded
   alongside this script in layout/theme.liquid. */

(function () {
  'use strict';

  const cache = new Map();

  /* Build storefront product URLs off the locale-aware root
     (window.Shopify.routes.root, e.g. "/" or "/en-ca/") so the quick view
     works under Markets/translated storefronts instead of hardcoding "/". */
  function productPath(handle) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + 'products/' + encodeURIComponent(handle);
  }

  /* The quick-view page is a full HTML document (minimal layout). Pull just
     the product-info element out of it so we don't inject <html>/<head>. */
  function extractContent(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector('.lmc-qv') || doc.querySelector('[id^="shopify-section-"]');
    return node ? node.outerHTML : html;
  }

  /* Minimal focus trap — mirrors Dawn's trapFocus but self-contained so
     we don't depend on global.js loading order. */
  function getFocusable(container) {
    return Array.from(
      container.querySelectorAll(
        'a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
      )
    ).filter(
      // offsetParent is null for position:fixed elements even when they're
      // visible, which would silently drop them from the trap cycle;
      // checkVisibility() (with a rects fallback) only excludes elements
      // that are actually display:none / not rendered.
      (el) =>
        !el.hasAttribute('disabled') &&
        (typeof el.checkVisibility === 'function' ? el.checkVisibility() : el.getClientRects().length > 0)
    );
  }

  class LMCQuickView extends HTMLElement {
    constructor() {
      super();
      this.onDocClick = this.onDocClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onClickInside = this.onClickInside.bind(this);
      this.onTrapKey = this.onTrapKey.bind(this);
    }

    connectedCallback() {
      this.contentEl = this.querySelector('[data-qv-content]');
      this.loadingEl = this.querySelector('[data-qv-loading]');
      this.modalEl = this.querySelector('.lmc-qv-modal');
      this.openerEl = null; // remember which button opened us so focus returns

      document.addEventListener('click', this.onDocClick);
      this.addEventListener('click', this.onClickInside);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('keydown', this.onTrapKey);
      this.removeEventListener('click', this.onClickInside);
      document.documentElement.classList.remove('lmc-qv-open');
    }

    onDocClick(event) {
      const trigger = event.target.closest('[data-quick-view-trigger]');
      if (!trigger) return;
      const handle = trigger.getAttribute('data-product-handle');
      // Without a handle, fall through to whatever native behavior the
      // element has (e.g., a link to the PDP) instead of swallowing the click.
      if (!handle) return;
      event.preventDefault();
      this.open(handle, trigger);
    }

    onClickInside(event) {
      if (event.target.closest('[data-qv-close]')) {
        this.close();
        return;
      }
      // Gallery: clicking a thumbnail swaps the active main image.
      const thumb = event.target.closest('[data-qv-thumb-index]');
      if (thumb) {
        this.activateSlide(thumb.getAttribute('data-qv-thumb-index'));
      }
    }

    activateSlide(index) {
      if (!this.contentEl) return;
      this.contentEl.querySelectorAll('[data-qv-slide-index]').forEach((slide) => {
        slide.classList.toggle('is-active', slide.getAttribute('data-qv-slide-index') === index);
      });
      this.contentEl.querySelectorAll('[data-qv-thumb-index]').forEach((thumb) => {
        const isActive = thumb.getAttribute('data-qv-thumb-index') === index;
        thumb.classList.toggle('is-active', isActive);
        if (isActive) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
      }
    }

    onTrapKey(event) {
      if (event.key !== 'Tab' || !this.isOpen) return;
      const focusable = getFocusable(this.modalEl);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    async open(handle, opener) {
      this.openerEl = opener;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('lmc-qv-open');
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('keydown', this.onTrapKey);
      this.isOpen = true;

      // Cancel any in-flight fetch from a prior open, then track this
      // open's token so a slow fetch that resolves AFTER a newer open
      // can't overwrite the modal content with stale HTML. This must
      // happen BEFORE the cache fast-path too: serving product B from
      // cache while product A's fetch is still in flight would otherwise
      // leave A's token current, and A's late response would clobber B.
      if (this.activeAbortController) {
        this.activeAbortController.abort();
        this.activeAbortController = null;
      }
      const openToken = Symbol('qv-open');
      this.currentOpenToken = openToken;

      if (cache.has(handle)) {
        this.renderContent(cache.get(handle));
        return;
      }

      const controller = new AbortController();
      this.activeAbortController = controller;

      this.showLoading();
      try {
        const url = productPath(handle) + '?view=quick-view';
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Quick view fetch failed: ' + response.status);
        const html = extractContent(await response.text());
        // Drop the result if the user opened a different product (or closed)
        // before this fetch came back.
        if (this.currentOpenToken !== openToken) return;
        cache.set(handle, html);
        this.renderContent(html);
      } catch (err) {
        if (err.name === 'AbortError') return;
        if (this.currentOpenToken !== openToken) return;
        this.contentEl.innerHTML =
          '<div class="lmc-qv-loading">Couldn\'t load this product. <a href="' +
          productPath(handle) +
          '">View full details →</a></div>';
        this.hideLoading();
        // eslint-disable-next-line no-console
        console.error('[lmc-quick-view]', err);
      }
    }

    showLoading() {
      if (this.loadingEl) this.loadingEl.hidden = false;
      this.contentEl.innerHTML = '';
    }

    /*
     * Client-side variant resolution for the modal. Dawn's product-info.js
     * resolves variants by re-fetching the product *section* by id, but the
     * quick-view section id doesn't exist on the standalone product page, so
     * the hidden id never commits and Add to Cart adds the wrong variant.
     * Here we read the embedded variants JSON and, on swatch change, update
     * the hidden id + price + Add to Cart button directly. (Dawn's
     * variant-selects still handles the swatch label optimistically.)
     */
    bindVariantPicker() {
      const root = this.contentEl;
      const variantSelects = root.querySelector('variant-selects');
      if (!variantSelects) return;

      let variants = [];
      variantSelects.querySelectorAll('script[type="application/json"]').forEach((script) => {
        if (script.hasAttribute('data-selected-variant')) return;
        try {
          const parsed = JSON.parse(script.textContent);
          if (Array.isArray(parsed)) variants = parsed;
        } catch (err) {
          /* malformed payload — leave variants empty */
        }
      });
      if (!variants.length) return;

      const idInput = root.querySelector('input[name="id"]');
      const priceEl = root.querySelector('.lmc-qv__price-wrap .lmc-pdp__price');
      const strikeEl = root.querySelector('.lmc-qv__price-wrap .lmc-pdp__price-strike');
      const atcButton = root.querySelector('button[name="add"]');
      const atcText = atcButton ? atcButton.querySelector('span') : null;

      const money = (cents, trimZeros) => {
        const whole = cents % 100 === 0;
        const digits = trimZeros && whole ? 0 : 2;
        return '$' + (cents / 100).toLocaleString('en-US', {
          minimumFractionDigits: digits,
          maximumFractionDigits: 2,
        });
      };

      // Baseline for option positions with no rendered picker: the modal only
      // renders swatches for the option literally named "Color", so any other
      // option (e.g. a Bundle) must stay pinned to the currently selected
      // variant's value. Without pinning, resolution falls to whichever
      // variant happens to match the color first in the variants array and
      // Add to Cart silently adds the wrong bundle.
      let currentVariant =
        variants.find((v) => idInput && String(v.id) === String(idInput.value)) || variants[0];

      variantSelects.addEventListener('change', () => {
        const chosen = [];
        variantSelects
          .querySelectorAll('input[type="radio"][data-option-index]:checked')
          .forEach((el) => {
            chosen[parseInt(el.dataset.optionIndex, 10)] = el.value;
          });
        if (!chosen.some((v) => v != null)) return;

        // Fill unrendered positions from the current variant, then resolve:
        // the fully-pinned combination is unique when it exists. If it
        // doesn't (sparse variant matrix), prefer the first AVAILABLE
        // variant matching the rendered choices over an arbitrary one.
        const pinned = currentVariant.options.map((value, i) => (chosen[i] != null ? chosen[i] : value));
        const matchesChosen = (v) => chosen.every((value, i) => value == null || v.options[i] === value);
        const match =
          variants.find((v) => pinned.every((value, i) => v.options[i] === value)) ||
          variants.find((v) => v.available && matchesChosen(v)) ||
          variants.find(matchesChosen);
        if (!match) return;
        currentVariant = match;

        if (idInput) idInput.value = match.id;
        if (priceEl) priceEl.textContent = money(match.price, false);
        if (strikeEl) {
          if (match.compare_at_price && match.compare_at_price > match.price) {
            strikeEl.textContent = money(match.compare_at_price, false);
            strikeEl.hidden = false;
          } else {
            strikeEl.hidden = true;
          }
        }
        if (atcButton) {
          atcButton.disabled = !match.available;
          if (atcText) {
            atcText.textContent = match.available
              ? `Add to cart · ${money(match.price, true)}`
              : 'Sold out';
          }
        }

        // Swap the main image to the selected variant's linked media. The QV
        // slides are tagged with data-media-id (lmc-quick-view.liquid) and
        // every variant's featured image is guaranteed to have a slide.
        const fm = match.featured_media;
        const mediaId = fm == null ? null : fm.id != null ? fm.id : fm;
        if (mediaId != null) {
          const slide = root.querySelector(`.lmc-qv__main-slide[data-media-id="${mediaId}"]`);
          if (slide) this.activateSlide(slide.getAttribute('data-qv-slide-index'));
        }
      });
    }

    /*
     * Delivery card picker — the modal's copy of <lmc-pdp-delivery>
     * (lmc-pdp.js), reimplemented here because lmc-pdp.js only loads on
     * product templates and the modal renders on every page. The radios sit
     * inside <variant-selects>, so the change event must be contained or
     * Dawn treats a delivery pick as a variant option change. The choice
     * is deliberately NOT persisted: every open defaults to free Curbside
     * (the cached modal HTML's markup default) — a White-Glove pick on
     * one product must not follow the shopper to the next.
     */
    bindDeliveryPicker() {
      const wrap = this.contentEl.querySelector('[data-qv-delivery]');
      if (!wrap) return;

      wrap.addEventListener('change', (event) => {
        event.stopPropagation();
        const input = event.target;
        if (!input || input.type !== 'radio') return;
        input.checked = true;
        wrap.querySelectorAll('.lmc-opt-card').forEach((card) => {
          card.classList.toggle('is-active', card.contains(input));
        });
      });
    }

    hideLoading() {
      if (this.loadingEl) this.loadingEl.hidden = true;
    }

    renderContent(html) {
      this.hideLoading();
      this.contentEl.innerHTML = html;

      // Dawn's product-info.js re-fetches the section and re-renders on every
      // option change, then calls focus() on the changed input. Inside the
      // modal that auto-scrolls the sheet (its overflow is clipped, so the
      // scroll just exposes empty space under "View full details") and it's
      // redundant -- bindVariantPicker() below resolves variants client-side.
      // Unsubscribe it so a colour pick doesn't trigger the fetch/scroll/
      // re-render. Replace with a no-op so disconnectedCallback stays safe.
      const productInfo = this.contentEl.querySelector('product-info');
      if (productInfo && typeof productInfo.onVariantChangeUnsubscriber === 'function') {
        productInfo.onVariantChangeUnsubscriber();
        productInfo.onVariantChangeUnsubscriber = () => {};
      }

      this.bindVariantPicker();
      this.bindDeliveryPicker();
      // The quantity stepper lives INSIDE <variant-selects> in the modal
      // markup, so its change event bubbles up and Dawn's VariantSelects
      // treats it as an option change — ProductInfo then re-fetches the
      // section and swaps in a server-rendered quantity input (value =
      // min), instantly undoing the increment. Contain the event at the
      // stepper: QuantityInput's own handler binds on the <input> itself,
      // so it has already run by the time this listener fires.
      this.contentEl.querySelectorAll('quantity-input').forEach((qty) => {
        qty.addEventListener('change', (event) => event.stopPropagation());
      });
      // Move focus to the first focusable element inside the modal so
      // keyboard users immediately enter the tab cycle.
      requestAnimationFrame(() => {
        const focusable = getFocusable(this.modalEl);
        const first = focusable.find((el) => !el.matches('[data-qv-close]')) || focusable[0];
        if (first) first.focus();
      });
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('lmc-qv-open');
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('keydown', this.onTrapKey);
      this.isOpen = false;
      // Invalidate any in-flight fetch token so a slow response can't render
      // into a closed modal.
      this.currentOpenToken = null;
      if (this.activeAbortController) {
        this.activeAbortController.abort();
        this.activeAbortController = null;
      }
      // Don't clear contentEl so cached re-open is instant; we'll overwrite on next open.
      if (this.openerEl && typeof this.openerEl.focus === 'function') {
        this.openerEl.focus();
      }
      this.openerEl = null;
    }
  }

  if (!customElements.get('lmc-quick-view')) {
    customElements.define('lmc-quick-view', LMCQuickView);
  }
})();
